<template>
  <div class="dispatch-line-details-tab">
    <p class="dispatch-line-details-tab__intro">
      按分码支展开；合并打码组内分码只展开一次。加载后在下方「列筛选」按列过滤（客户、状态、已送货等），与表格列显示独立；导出按当前筛选结果。
    </p>

    <div class="toolbar">
      <el-button type="primary" size="small" :loading="loading" @click="loadData">
        <el-icon><Refresh /></el-icon> 刷新
      </el-button>
      <el-button size="small" :loading="exporting" :disabled="loading" @click="handleExportExcel">导出</el-button>
      <el-button size="small" @click="showColumnConfig = true">
        <el-icon><Setting /></el-icon> 列
      </el-button>
      <el-button size="small" :type="showColFilters ? 'primary' : 'default'" plain @click="showColFilters = !showColFilters">
        列筛选
      </el-button>
      <el-button v-if="hasActiveColFilters" size="small" link type="danger" @click="clearColumnFilters">清空列筛</el-button>
      <span v-if="filteredCount !== allRowsCache.length" class="toolbar__hint">
        筛选 {{ filteredCount }} / {{ allRowsCache.length }} 行
      </span>
    </div>

    <el-alert
      v-if="meta.orders_truncated || meta.rows_truncated"
      type="warning"
      :closable="false"
      show-icon
      class="dispatch-line-details-tab__warn"
      title="数据已截断"
    >
      <template #default>
        <span v-if="meta.orders_truncated">派工单超过扫描上限，请用列筛选缩小范围。</span>
        <span v-if="meta.rows_truncated">行数超过上限。</span>
        已扫 {{ meta.orders_scanned }}/{{ meta.orders_matched }} 单。
      </template>
    </el-alert>

    <div v-show="showColFilters" class="col-filters">
      <div class="col-filters__grid">
        <div v-for="col in filterableColumns" :key="col.prop" class="col-filters__item">
          <label class="col-filters__lbl" :title="col.labelFull || col.label">{{ col.labelFull || col.label }}</label>
          <el-select
            v-if="col.filterType === 'select' || col.filterType === 'yn'"
            v-model="columnFilters[col.prop]"
            clearable
            filterable
            size="small"
            :placeholder="col.label"
            class="col-filters__input"
            @change="onColumnFilterChange"
          >
            <el-option v-for="opt in colFilterOptions(col)" :key="`${col.prop}-${opt}`" :label="opt" :value="opt" />
          </el-select>
          <el-input
            v-else
            v-model="columnFilters[col.prop]"
            clearable
            size="small"
            :placeholder="col.label"
            class="col-filters__input"
            @clear="onColumnFilterChange"
            @keyup.enter="onColumnFilterChange"
          />
        </div>
      </div>
    </div>

    <el-table
      ref="tableRef"
      v-loading="loading"
      :data="tablePageRows"
      border
      stripe
      class="dispatch-line-table"
      size="small"
      :row-class-name="rowClassName"
      @header-dragend="onHeaderDragEnd"
    >
      <template v-for="col in visibleColumns" :key="col.prop">
        <el-table-column
          :column-key="col.prop"
          :prop="col.prop"
          :width="col.width"
          :min-width="col.minWidth"
          :align="col.align"
          :fixed="col.fixed"
          show-overflow-tooltip
        >
          <template #header>
            <el-tooltip :content="col.labelFull || col.label" placement="top" :show-after="400">
              <span class="col-hdr">{{ col.label }}</span>
            </el-tooltip>
          </template>
          <template v-if="col.slot === 'merged_quantity'" #default="{ row }">
            <span :class="{ 'merge-qty-hl': row.is_merge_group }">{{ formatNum(row.merged_quantity) }}</span>
          </template>
          <template v-else-if="col.slot === 'merge_group'" #default="{ row }">
            <el-tag v-if="row.is_merge_group" type="warning" size="small">{{ row.merge_group_line_count }}并</el-tag>
            <span v-else class="muted">—</span>
          </template>
          <template v-else-if="col.slot === 'yn_weigh'" #default="{ row }">
            {{ row.packaging_require_weigh ? '是' : '否' }}
          </template>
          <template v-else-if="col.slot === 'yn_volume'" #default="{ row }">
            {{ row.packaging_require_volume ? '是' : '否' }}
          </template>
          <template v-else-if="col.slot === 'yn_fold'" #default="{ row }">
            {{ row.branch_fold_compensation ? '是' : '否' }}
          </template>
          <template v-else #default="{ row }">
            {{ formatCell(row, col) }}
          </template>
        </el-table-column>
      </template>
    </el-table>

    <div class="pagination-section">
      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.perPage"
        :total="filteredCount"
        :page-sizes="[50, 100, 200, 500]"
        layout="total, sizes, prev, pager, next, jumper"
        small
        @size-change="onPageSizeChange"
        @current-change="onPageChange"
      />
    </div>

    <ColumnConfigDialog
      :show="showColumnConfig"
      :column-config="columnConfig"
      page-title="派工明细汇总"
      :local-storage-key="`${columnStorageKey}_columns_config`"
      dialog-width="640px"
      @update:show="showColumnConfig = $event"
      @config-change="onColumnConfigChange"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { Refresh, Setting } from '@element-plus/icons-vue'
import { getDispatchOrderLineDetails } from '@/api/dispatchOrders'
import { exportDispatchLineDetailsToExcel } from '@/utils/exportDispatchLineDetailsExcel'
import ColumnConfigDialog from '@/components/ColumnConfigDialog.vue'
import { getUserScopedStorageKey } from '@/utils/userScopedStorage'
import {
  DISPATCH_LINE_DETAIL_FILTER_COLUMNS,
  applyLineDetailColumnFilters,
  getCellDisplayValue,
  loadLineDetailColumnConfig,
  resolveLineDetailColumnFilterOptions,
  saveLineDetailColumnConfig
} from '@/utils/dispatchLineDetailsColumns'

defineOptions({ name: 'DispatchOrderLineDetailsTab' })

const columnStorageKey = getUserScopedStorageKey('dispatch_line_details')
const tableRef = ref(null)
const loading = ref(false)
const exporting = ref(false)
const showColumnConfig = ref(false)
const showColFilters = ref(true)
const allRowsCache = ref([])
const columnConfig = ref(loadLineDetailColumnConfig(columnStorageKey))
const columnFilters = reactive({})

const pagination = reactive({
  page: 1,
  perPage: 100
})

const meta = reactive({
  orders_scanned: 0,
  orders_matched: 0,
  orders_truncated: false,
  rows_truncated: false
})

const visibleColumns = computed(() => columnConfig.value.filter((c) => c.visible !== false))

const filterableColumns = computed(() => DISPATCH_LINE_DETAIL_FILTER_COLUMNS)

const filteredRows = computed(() =>
  applyLineDetailColumnFilters(allRowsCache.value, columnFilters, DISPATCH_LINE_DETAIL_FILTER_COLUMNS)
)

const filteredCount = computed(() => filteredRows.value.length)

const tablePageRows = computed(() => {
  const start = (pagination.page - 1) * pagination.perPage
  return filteredRows.value.slice(start, start + pagination.perPage)
})

const hasActiveColFilters = computed(() =>
  Object.values(columnFilters).some((v) => v != null && String(v).trim() !== '')
)

function colFilterOptions(col) {
  return resolveLineDetailColumnFilterOptions(col, allRowsCache.value)
}

function initColumnFilters() {
  DISPATCH_LINE_DETAIL_FILTER_COLUMNS.forEach((c) => {
    if (columnFilters[c.prop] === undefined) columnFilters[c.prop] = ''
  })
}

function formatNum(v) {
  if (v == null || v === '') return '—'
  return Number(v).toLocaleString()
}

function formatCell(row, col) {
  const s = getCellDisplayValue(row, col)
  return s === '' ? '—' : s
}

function rowClassName({ row }) {
  return row.is_merge_group ? 'dispatch-line-row--merge' : ''
}

function onColumnFilterChange() {
  pagination.page = 1
}

function clearColumnFilters() {
  DISPATCH_LINE_DETAIL_FILTER_COLUMNS.forEach((c) => {
    columnFilters[c.prop] = ''
  })
  pagination.page = 1
}

function onPageSizeChange() {
  pagination.page = 1
}

function onPageChange() {
  /* computed 自动更新 */
}

function onColumnConfigChange(newConfig) {
  if (!Array.isArray(newConfig)) return
  columnConfig.value = newConfig.map((c) => ({ ...c }))
  saveLineDetailColumnConfig(columnStorageKey, columnConfig.value)
  nextTick(() => tableRef.value?.doLayout?.())
}

function onHeaderDragEnd(newWidth, oldWidth, column) {
  const key = column?.columnKey || column?.property
  if (!key) return
  const col = columnConfig.value.find((c) => c.prop === key)
  if (col && newWidth) {
    col.width = Math.round(newWidth)
    saveLineDetailColumnConfig(columnStorageKey, columnConfig.value)
  }
}

async function loadData() {
  loading.value = true
  try {
    const res = await getDispatchOrderLineDetails({ export_all: 'true', max_orders: 800 })
    if (res.success) {
      allRowsCache.value = Array.isArray(res.data) ? res.data : []
      pagination.page = 1
      const m = res.meta || {}
      meta.orders_scanned = m.orders_scanned ?? 0
      meta.orders_matched = m.orders_matched ?? 0
      meta.orders_truncated = !!m.orders_truncated
      meta.rows_truncated = !!m.rows_truncated
    } else {
      ElMessage.error(res.message || '加载失败')
      allRowsCache.value = []
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '加载失败')
    allRowsCache.value = []
  } finally {
    loading.value = false
    nextTick(() => tableRef.value?.doLayout?.())
  }
}

async function handleExportExcel() {
  exporting.value = true
  try {
    const rows = filteredRows.value
    if (!rows.length) {
      ElMessage.warning('当前无数据可导出')
      return
    }
    const fn = exportDispatchLineDetailsToExcel(rows, columnConfig.value)
    ElMessage.success(`已导出 ${rows.length} 行：${fn}`)
  } catch (e) {
    ElMessage.error(e.message || '导出失败')
  } finally {
    exporting.value = false
  }
}

onMounted(() => {
  initColumnFilters()
  loadData()
})
</script>

<style scoped>
.dispatch-line-details-tab__intro {
  margin: 0 0 8px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  line-height: 1.4;
}

.dispatch-line-details-tab__warn {
  margin-bottom: 8px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
  align-items: center;
}

.toolbar__hint {
  font-size: 12px;
  color: var(--el-color-primary);
}

.col-filters {
  margin-bottom: 8px;
  padding: 8px 10px;
  background: var(--el-fill-color-lighter);
  border-radius: 4px;
  max-height: 280px;
  overflow: auto;
}

.col-filters__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(152px, 1fr));
  gap: 6px 10px;
}

.col-filters__item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.col-filters__lbl {
  font-size: 11px;
  color: var(--el-text-color-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.col-filters__input {
  width: 100%;
}

.col-hdr {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.dispatch-line-table :deep(.el-table__header th) {
  padding: 4px 0;
}

.dispatch-line-table :deep(.el-table__header .cell) {
  padding: 0 4px;
  line-height: 1.2;
}

.dispatch-line-table :deep(.el-table__body td .cell) {
  padding: 0 4px;
  font-size: 12px;
  line-height: 1.35;
}

.pagination-section {
  margin-top: 8px;
  display: flex;
  justify-content: flex-end;
}

.merge-qty-hl {
  font-weight: 600;
  color: var(--el-color-warning);
}

.muted {
  color: var(--el-text-color-secondary);
}

:deep(.dispatch-line-row--merge) {
  background-color: var(--el-color-warning-light-9);
}
</style>
